import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * CookieConsentContext
 * NDPA / GDPR aligned cookie consent state management.
 *
 * Storage key: dfl_cookie_consent
 * Schema: {
 *   version: 1,
 *   timestamp: ISO string,
 *   essential: true,            // always true, locked
 *   analytics: bool,
 *   advertising: bool,
 *   personalization: bool,
 *   functional: bool
 * }
 */

const STORAGE_KEY = 'dfl_cookie_consent';
const CONSENT_VERSION = 1;

export const DEFAULT_PREFERENCES = {
  essential: true,
  analytics: false,
  advertising: false,
  personalization: false,
  functional: false,
};

const CookieConsentContext = createContext(null);

export const useCookieConsent = () => {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error('useCookieConsent must be used inside CookieConsentProvider');
  return ctx;
};

export const CookieConsentProvider = ({ children }) => {
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [hasDecided, setHasDecided] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.version === CONSENT_VERSION) {
          setPreferences({
            essential: true,
            analytics: !!parsed.analytics,
            advertising: !!parsed.advertising,
            personalization: !!parsed.personalization,
            functional: !!parsed.functional,
          });
          setHasDecided(true);
          setShowBanner(false);
          return;
        }
      }
      // No valid decision yet -> show banner
      setShowBanner(true);
    } catch (_e) {
      setShowBanner(true);
    }
  }, []);

  const persist = useCallback((prefs, source = 'banner') => {
    const payload = {
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      essential: true,
      analytics: !!prefs.analytics,
      advertising: !!prefs.advertising,
      personalization: !!prefs.personalization,
      functional: !!prefs.functional,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (_e) {
      // ignore storage errors
    }
    setPreferences({ ...payload });
    setHasDecided(true);
    setShowBanner(false);
    setShowPreferences(false);

    // Fire and forget audit log (NDPA / GDPR audit trail)
    // Never blocks UI; logs warning if it fails (visible to monitoring).
    try {
      axios.post(
        `${API}/consent/log`,
        {
          event_type: 'cookie_preferences',
          source,
          preferences: {
            essential: true,
            analytics: !!prefs.analytics,
            advertising: !!prefs.advertising,
            personalization: !!prefs.personalization,
            functional: !!prefs.functional,
          },
        },
        { withCredentials: true }
      ).catch((err) => console.warn('consent log failed:', err?.message || err));
    } catch (e) { console.warn('consent log error:', e); }
  }, []);

  const acceptAll = useCallback(() => {
    persist({ analytics: true, advertising: true, personalization: true, functional: true }, 'banner');
  }, [persist]);

  const rejectAll = useCallback(() => {
    persist({ analytics: false, advertising: false, personalization: false, functional: false }, 'banner');
  }, [persist]);

  const savePreferences = useCallback((prefs) => {
    persist(prefs, 'modal');
  }, [persist]);

  const openPreferences = useCallback(() => setShowPreferences(true), []);
  const closePreferences = useCallback(() => setShowPreferences(false), []);
  const reopenBanner = useCallback(() => {
    setShowPreferences(true);
  }, []);

  const value = {
    preferences,
    hasDecided,
    showBanner,
    showPreferences,
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferences,
    closePreferences,
    reopenBanner,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export default CookieConsentProvider;
